import * as routeHelpers from "../../lib/routeHelpers";
import {Lib} from "habit-core";
import {Outlet, useParams, useNavigate} from "react-router-dom";
import {useAppSelector} from "../../lib/hooks";
import cn from "classnames";
import logger from "../../lib/sentryLogger";
import MenuComboLink from "../MenuComboLink";
import MenuGroupingLink from "../MenuGroupingLink";
import MenuItemLink from "../MenuItemLink";
import React, {useMemo, useEffect} from "react";
import styles from "./MenuCategoryItems.module.css";

type Props = {
    className?: string;
};

type Params = {
    categoryId: string;
};

export default function MenuCategoryItems(props: Props) {
    const navigate = useNavigate();
    const {categoryId} = useParams<Params>();
    const menuId = useAppSelector((state) => state.currentOrder.menuId);
    const categoriesById = useAppSelector((state) => state.categories.byId);
    useEffect(() => {
        if (
            !menuId ||
            !categoryId ||
            (categoryId !== routeHelpers.OFF_MENU_CATEGORY_ID &&
                !categoriesById[categoryId])
        ) {
            logger.warn(
                "Attempting to view category products. Invalid menu id menuId, and/or category id",
                {
                    menuId: menuId,
                    categoryId: categoryId,
                },
            );
            navigate(routeHelpers.menu(), {replace: true});
        }
    }, []);

    const getCategoryProducts = useMemo(
        () =>
            Lib.selectors.createGetMenuCategoryProducts(
                false,
                true,
                false,
                true,
            ),
        [],
    );

    const getOffMenuProducts = useMemo(
        () => Lib.selectors.getOffMenuCombosAndItems(),
        [],
    );

    const categoryProducts = useAppSelector((state) =>
        categoryId === routeHelpers.OFF_MENU_CATEGORY_ID
            ? getOffMenuProducts(state, menuId ?? "")
            : getCategoryProducts(state, categoryId ?? "", menuId ?? ""),
    );

    const displaySize = categoryProducts.length > 10 ? "small" : "regular";
    const numColumns = categoryProducts.length > 12 ? 3 : 2;

    if (
        !menuId ||
        !categoryId ||
        (categoryId !== routeHelpers.OFF_MENU_CATEGORY_ID &&
            !categoriesById[categoryId])
    ) {
        return null;
    }

    return (
        <div className={cn(styles.container, props.className)}>
            {categoryProducts.map((x) => {
                if (x.type === "combo" && x.comboId) {
                    return (
                        <MenuComboLink
                            key={x.comboId}
                            comboId={x.comboId}
                            categoryId={categoryId}
                            menudId={menuId}
                            numColumns={numColumns}
                            displaySize={displaySize}
                        />
                    );
                } else if (x.type === "item" && x.itemId) {
                    return (
                        <MenuItemLink
                            key={x.itemId}
                            itemId={x.itemId}
                            categoryId={categoryId}
                            menuId={menuId}
                            numColumns={numColumns}
                            displaySize={displaySize}
                        />
                    );
                } else if (x.type === "grouping") {
                    return (
                        <MenuGroupingLink
                            key={x.groupingId}
                            groupingId={x.groupingId}
                            categoryId={categoryId}
                            menuId={menuId}
                            numColumns={numColumns}
                            displaySize={displaySize}
                        />
                    );
                } else {
                    // this is highly unlikely, just logging locally for now.
                    console.warn(
                        "Viewing category products. Encountered unknown section product data.",
                        {
                            sectionType: x.type,
                            categoryId: categoryId,
                        },
                    );
                }

                return null;
            })}

            <Outlet />
            <div
                className={numColumns === 2 ? styles.spacer : styles.spacer3Col}
            />
            <div
                className={numColumns === 2 ? styles.spacer : styles.spacer3Col}
            />
            <div
                className={numColumns === 2 ? styles.spacer : styles.spacer3Col}
            />
            <div
                className={numColumns === 2 ? styles.spacer : styles.spacer3Col}
            />
        </div>
    );
}
